import React, { HTMLProps } from 'react';
import { Stock } from '../../../../types/Stock';
import { mapTopText, mapSectionPages, SectionsContainer, Section } from '../comparison-bars/FundSectionFunctions';
import { SectionDisclosure } from '../../../general/Disclosure';
import {
    ComparisonStickyCardDetailsBasicCharacteristics,
    ComparisonStickyCardDetailsDividendPayments,
    ComparisonStickyCardDetailsHistoricalReturns,
    ComparisonStickyCardDetailsHistoricalReturnsSmall,
    ComparisonStickyCardDetailsStrategy,
    ComparisonStickyCardDetailsUnderlyingStocks,
} from '../comparison-sticky-card/ComparisonStickyCardDetails';
import { ComparisonStickyCardDetailsRiskCharacteristics } from '../comparison-sticky-card/ComparisonStickyCardDetails';
import { useTranslation } from 'react-i18next';
import { mergedItems } from '../../../../utils/functions';
import { DateTime } from 'luxon';

export type TintedStock = Stock & {
    tint: string;
};

export const StockCompare: React.FC<
    {
        stocks: TintedStock[];
        startDate: string;
        endDate?: string;
    } & HTMLProps<HTMLDivElement>
> = ({ className, stocks, startDate, endDate = 'Nu' }) => {
    const { t } = useTranslation('funds');

    const StockTopText = stocks.map((section, index) => mapTopText(section, index, startDate, endDate));

    const StockStrategy = stocks.map((stock, index) => {
        return (
            <div className={`flex-1 px-4 py-1 ${index > 0 ? 'lg:border-l-2 border-gray-300' : ''}`}>
                <ComparisonStickyCardDetailsStrategy asset={stock} />
            </div>
        );
    });

    const StockCoreProperties = stocks.map((stock, index) => {
        return (
            <div className={`flex-1 px-4 py-1 ${index > 0 ? 'lg:border-l-2 border-gray-300' : ''}`}>
                <ComparisonStickyCardDetailsBasicCharacteristics asset={stock} />
            </div>
        );
    });

    const StockSpecifics = stocks.map((stock, index) => {
        return (
            <div className={`flex-1 px-4 py-1 ${index > 0 ? 'lg:border-l-2 border-gray-300' : ''}`}>
                <ComparisonStickyCardDetailsUnderlyingStocks asset={stock} />
            </div>
        );
    });

    const StockRiskCharactaristic = stocks.map((stock, index) => {
        return (
            <div className={`flex-1 px-4 py-1 ${index > 0 ? 'lg:border-l-2 border-gray-300' : ''}`}>
                <ComparisonStickyCardDetailsRiskCharacteristics asset={stock} />
            </div>
        );
    });

    const StockHistoricalReturns = stocks.map((stock, index) => {
        return (
            <div className={`flex-1 px-4 py-1 ${index > 0 ? 'lg:border-l-2 border-gray-300' : ''}`}>
                <ComparisonStickyCardDetailsHistoricalReturns className="hidden sm:grid" asset={stock} />
                <ComparisonStickyCardDetailsHistoricalReturnsSmall className="sm:hidden" asset={stock} />
            </div>
        );
    });

    const StockDividends = stocks.map((stock, index) => {
        return (
            <div className={`flex-1 px-4 py-1 ${index > 0 ? 'lg:border-l-2 border-gray-300' : ''}`}>
                <ComparisonStickyCardDetailsDividendPayments fund={stock} />
            </div>
        );
    });

    const StockHoldings = stocks.map((stock, index) =>
        mapSectionPages({
            index,
            sections: [
                {
                    title: 'Verdeling bedrijven',
                    items: stock.holdings || [],
                    options: { showChart: false, showColoredCircles: false },
                    referenceDate: DateTime.fromISO(stock.referenceDateHoldings),
                },
            ],
            appendix: 'total',
        }),
    );

    const StockSectors = stocks.map((stock, index) => {
        return mapSectionPages({
            index,
            sections: [
                {
                    title: 'Verdeling sectoren',
                    items: mergedItems(
                        stocks.map((s) => s.sectors || []),
                        stock.sectors || [],
                    ),
                    referenceDate: DateTime.fromISO(stock.referenceDateSectors),
                },
            ],
            appendix: 'remaining',
        });
    });

    const StockRegionDistribution = stocks.map((stock, index) =>
        mapSectionPages({
            index,
            sections: [
                {
                    title: t('funds-comparison-screen-markets-ring'),
                    items: mergedItems(
                        stocks.map((s) => s.regions || []),
                        stock.regions || [],
                    ),
                    referenceDate: DateTime.fromISO(stock.referenceDateRegions),
                },
            ],
        }),
    );

    const sections = [
        { title: t('funds-comparison-screen-investment-strategy'), nodes: StockStrategy, mobileOnly: true },
        { title: t('funds-comparison-screen-core-properties'), nodes: StockCoreProperties, mobileOnly: true },
        { title: t('funds-comparison-screen-underlying-stocks'), nodes: StockSpecifics, mobileOnly: true },
        { title: t('funds-comparison-screen-risk-characteristics'), nodes: StockRiskCharactaristic, mobileOnly: true },
        { title: t('funds-comparison-screen-historical-returns'), nodes: StockHistoricalReturns, mobileOnly: true },
        { title: t('funds-comparison-screen-dividend-payments'), nodes: StockDividends, mobileOnly: true },
        { title: t('funds-comparison-screen-holdings'), nodes: StockHoldings },
        { title: t('funds-comparison-screen-sectors'), nodes: StockSectors },
        { title: t('funds-comparison-screen-markets'), nodes: StockRegionDistribution },
    ];

    return (
        <SectionsContainer className={className}>
            <Section pages={StockTopText} />
            {sections.map((section, index) => {
                return (
                    <SectionDisclosure
                        title={section.title}
                        key={index}
                        className={`${section.mobileOnly === true ? 'xl:hidden' : ''}`}
                    >
                        <div className="flex flex-1 flex-col">
                            <Section pages={section.nodes} />
                        </div>
                    </SectionDisclosure>
                );
            })}
        </SectionsContainer>
    );
};

// function mapStockCompanies(data: TintedStock, index: number): React.ReactNode {
//     if (data.holdings === undefined || data.holdings.length === 0)
//         return <EmptyColumn className="px-8 py-4" index={index} />;
//     return (
//         <FundCompareList
//             sections={[
//                 {
//                     items:
//                         data.holdings?.map((holding, i) => {
//                             return {
//                                 ...holding,
//                                 tint: COLORS.total[i],
//                             };
//                         }) || [],
//                 },
//             ]}
//             appendix="total"
//             showColoredCircles={false}
//             className={`${index > 0 ? 'md:border-l-2 border-gray-300' : ''}`}
//         />
//     );
// }
